import type { FounderEstado, FounderTier } from '../../../api';
import { idiomaAtual, numero, t } from '../../../i18n/idioma';

/**
 * Os três Founder Packs no site (proposta-founders-pack §00, techspec-founders-pack §06). O conteúdo de cada
 * pack é fixo (o servidor tem a mesma tabela em FounderCatalog.cs); o preço e o "ativo" vêm da vitrine.
 */
export interface TierVisual {
  readonly id: FounderTier;
  /** Cor do selo e do filete do cartão: bronze, prata e ouro. */
  readonly metal: string;
  /** Cor do manto, no brilho do cabeçalho do cartão. */
  readonly robe: string;
}

export const TIERS: readonly TierVisual[] = [
  { id: 'peregrino', metal: '#c08a4a', robe: '#3e5b3a' },
  { id: 'cavaleiro', metal: '#d5dbe3', robe: '#2c4778' },
  { id: 'lorde', metal: '#e6c452', robe: '#6a1d29' }
];

export const tierDe = (id: FounderTier): TierVisual => TIERS.find(x => x.id === id) ?? TIERS[0];
export const nivel = (id: FounderTier | null | undefined): number => (id ? TIERS.findIndex(x => x.id === id) + 1 : 0);

const PASTA = '/entrada/landing/founders';
export const imagem = (nome: string) => `${PASTA}/${nome}.png`;
export const TRIO = { webp: `${PASTA}/trio.webp`, jpg: `${PASTA}/trio.jpg` };

export const reais = (centavos: number) =>
  (centavos / 100).toLocaleString(idiomaAtual(), { style: 'currency', currency: 'BRL' });

export const nomeDo = (id: FounderTier) => t(`landing.fund.tier.${id}`);
export const mantoDo = (id: FounderTier) => t(`landing.fund.manto.${id}`);

export type Pack = FounderEstado['packs'][number];
export const packDe = (estado: FounderEstado, id: FounderTier): Pack | undefined => estado.packs.find(p => p.id === id);

/** O que o site mostra como valor: o preço, ou a diferença na subida (nunca abaixo de R$ 1,00, como o servidor). */
export function valorDe(estado: FounderEstado, id: FounderTier, atual: FounderTier | null): number {
  const novo = packDe(estado, id)?.centavos ?? 0;
  const antes = atual ? packDe(estado, atual)?.centavos ?? 0 : 0;
  return Math.max(100, novo - antes);
}

export type Icone = 'vip' | 'selo' | 'corcel' | 'manto' | 'ouro' | 'cajado' | 'ok';
/** `exclusivo`: item de fundador que nunca mais volta (selo, corcéis, mantos e cajado; proposta-founders-pack). */
export interface Item { readonly icone: Icone; readonly texto: string; readonly nota?: string; readonly exclusivo?: boolean }

/** A lista do cartão de cada pack: corcel, manto e selo são só do próprio pack (os benefícios de VIP, ouro e extras crescem). */
export function itensDo(id: FounderTier, pack: Pack | undefined): Item[] {
  const n = nivel(id);
  const vip = pack?.vipDias ?? [30, 90, 180][n - 1];
  const ouro = pack?.ouro ?? [500, 1200, 2500][n - 1];
  const itens: Item[] = [];
  // Cada pack lista tudo o que dá (Renato, 24/10/2026): sem "Tudo do Peregrino", porque corcel, manto e selo são os do
  // próprio pack, não os do anterior.
  itens.push({ icone: 'vip', texto: t('landing.fund.item.vip', { n: numero(vip) }), nota: t('landing.fund.item.vipNota') });
  itens.push({ icone: 'selo', texto: t(`landing.fund.item.selo.${id}`), nota: t('landing.fund.item.seloNota'), exclusivo: true });
  itens.push({ icone: 'corcel', texto: t('landing.fund.corcel', { nome: nomeDo(id) }), nota: t(`landing.fund.item.corcelNota.${id}`), exclusivo: true });
  itens.push({ icone: 'manto', texto: t('landing.fund.item.manto', { cor: mantoDo(id) }), exclusivo: true });
  itens.push({ icone: 'ouro', texto: t('landing.fund.item.ouro', { n: numero(ouro) }) });
  if (n >= 3) itens.push({ icone: 'cajado', texto: t('landing.fund.item.cajado'), nota: t('landing.fund.item.cajadoNota'), exclusivo: true });
  itens.push({ icone: 'ok', texto: t('landing.fund.item.mural'), nota: t('landing.fund.item.muralNota') });
  // Cargo no Discord em todos os packs, Peregrino incluso (Renato, 25/10/2026: "acreditou no servidor").
  itens.push({ icone: 'ok', texto: t('landing.fund.item.discord') });
  if (n >= 2) itens.push({ icone: 'ok', texto: t('landing.fund.item.vaga') });
  if (n >= 3) itens.push({ icone: 'ok', texto: t('landing.fund.item.reserva') });
  return itens;
}

/** O código de erro do servidor (`founder.x`) vira texto do site; o resto cai na mensagem genérica. */
export function chaveDoErro(code: string | undefined): string | null {
  const m = code ? /^founder\.([a-zA-Z0-9]+)$/.exec(code) : null;
  return m ? `landing.fund.erro.${m[1]}` : null;
}
